import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider, type SubmitHandler, type FieldPath } from 'react-hook-form';
import Box from '@mui/material/Box';
import omit from 'lodash/omit';
import some from 'lodash/some';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Children } from '@repo/shared/types';
import { MerchantDataPayload } from '@repo/api/endpoints/merchant';
import { Button } from '@repo/ui';
import { useLiberisContext } from '@repo/app-contexts/liberis';
import { areAllProviderNotPromoted } from '@repo/store/helpers';
import { useProviderStore } from '@repo/store/provider';

import { useMainHubStoreState, useMainHubStoreActions } from '@/store/main-hub.store';
import { P24MarketingAgreementsFormLiberis } from '@/components/welcome/components/p24-marketing-agreements-form/liberis/p24-marketing-agreements-form-liberis';
import { useLoanProcessStore } from '@/components/loan-process/store/loan-process.store';
import { VIEW_TYPE } from '@/store/view.type';
import { useWelcomeViewWrapperContext } from '@/components/welcome/components/welcome-view-wrapper/context/use-welcome-view-wrapper-context';
import { useP24MarketingAgreementsFormLiberis } from '@/components/welcome/components/p24-marketing-agreements-form/liberis/use-p24-marketing-agreements-form-liberis';
import { useModalStore } from '@/components/modal/modal.store';

import { isMerchantContactDataEmpty } from '../../welcome/loan-application-possible/liberis/common/helpers/is-merchant-contact-data-empty';
import { formStepsFields } from './form-steps-fields';
import {
  emptyPragmaAgreementsSchema,
  requestedLoanFormSchemaWithP24Agreements,
  RequestedLoanValues,
  RequestedLoanValuesWithP24Agreements,
} from './form.schema';

interface LoanApplicationPossibleLiberisFormProps extends Children {
  withSubmit?: boolean;
  buttonText?: string;
  onButtonClick?: (values: RequestedLoanValuesWithP24Agreements) => void;
  onSubmit?: (values: RequestedLoanValuesWithP24Agreements) => void;
  withMarketingAgreementForm?: boolean;
  initialValues?: RequestedLoanValuesWithP24Agreements;
}

const p24MarketingAgreementsFormFieldsNames = ['email', 'selectAllCheckbox', 'sms', 'tel'];

export const LoanApplicationPossibleLiberisForm = ({
  withSubmit,
  children,
  buttonText,
  onButtonClick,
  onSubmit,
  withMarketingAgreementForm,
  initialValues,
}: LoanApplicationPossibleLiberisFormProps) => {
  const { t } = useTranslation();
  const { merchantResponseData, currentViewType } = useMainHubStoreState();
  const {
    state: { isAvailableMultiprovider },
  } = useProviderStore();
  const { setCurrentView } = useMainHubStoreActions();
  const { liberisStage, activeBeneficientIndex } = useLiberisContext();
  const {
    actions: { setRequestedLoanValues },
  } = useLoanProcessStore();
  const submitP24MarketingAgreementsForm = useP24MarketingAgreementsFormLiberis();
  const { additionalProps, closeModal } = useModalStore();

  const renderP24MarktingAgreementsForm =
    !isMerchantContactDataEmpty(merchantResponseData?.data?.merchantData) &&
    withMarketingAgreementForm;

  const { isP24MarketingAgreementsFormAvailable, setIsP24MarketingAgreementsFormAvailable } =
    useWelcomeViewWrapperContext();

  const schema =
    merchantResponseData &&
    areAllProviderNotPromoted(merchantResponseData) &&
    isAvailableMultiprovider &&
    liberisStage === 'AMOUNT'
      ? z.any()
      : z.object({
          ...requestedLoanFormSchemaWithP24Agreements.shape,
          ...emptyPragmaAgreementsSchema.shape,
        });

  const methods = useForm({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const p24MarketingAgreementsFormValues = {
    email: methods.watch('email'),
    sms: methods.watch('sms'),
    tel: methods.watch('tel'),
  };

  const handleSubmitP24MarketingAgreementsForm = () => {
    if (
      isP24MarketingAgreementsFormAvailable &&
      some(p24MarketingAgreementsFormValues, (value) => value)
    ) {
      submitP24MarketingAgreementsForm(p24MarketingAgreementsFormValues);
      setIsP24MarketingAgreementsFormAvailable(false);
    }
  };

  const handleNextStep = async () => {
    let stepFieldsByType: string[];

    const stepFields = formStepsFields[liberisStage];

    if (typeof stepFields === 'function') {
      if (merchantResponseData && liberisStage === 'AGREEMENTS_PRAGMA') {
        stepFieldsByType = (stepFields as (data: MerchantDataPayload) => string[])(
          merchantResponseData.data,
        );
      } else if (liberisStage === 'BENEFICIENT_DATA') {
        stepFieldsByType = (stepFields as (index: number) => string[])(activeBeneficientIndex);
      } else {
        throw new Error(`Unexpected functional stage: ${liberisStage}`);
      }
    } else {
      stepFieldsByType = stepFields;
    }

    const isValid = await methods.trigger(
      stepFieldsByType as FieldPath<RequestedLoanValuesWithP24Agreements>[],
    );

    if (isValid) {
      // Clear errors because onChange mode on useForm validating all form. But isValid validates only specific fields
      methods.clearErrors();
      handleSubmitP24MarketingAgreementsForm();
      const formValues = methods.getValues();
      onButtonClick?.(formValues as RequestedLoanValuesWithP24Agreements);
    }
  };

  const handleValidSubmit: SubmitHandler<RequestedLoanValuesWithP24Agreements> = (data) => {
    onSubmit?.(data);
    const requestedLoanValues = omit(
      data,
      p24MarketingAgreementsFormFieldsNames,
    ) as RequestedLoanValues;
    handleSubmitP24MarketingAgreementsForm();
    setRequestedLoanValues(requestedLoanValues);
    setCurrentView(VIEW_TYPE.SELECT_OFFER_VIEW);

    if (
      currentViewType === 'SELECT_OFFER_VIEW' &&
      typeof additionalProps?.handleMutateLoanOffer === 'function'
    ) {
      additionalProps?.handleMutateLoanOffer();
      closeModal();
    }
  };
  const onButtonClickHandler = () => {
    void handleNextStep();
  };

  return (
    <FormProvider {...methods}>
      <form
        style={{ height: '100%' }}
        onSubmit={(event) => void methods.handleSubmit(handleValidSubmit)(event)}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box>{children}</Box>
          <Box>
            {isP24MarketingAgreementsFormAvailable && renderP24MarktingAgreementsForm ? (
              <P24MarketingAgreementsFormLiberis />
            ) : null}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: { xs: 2, sm: '20px', md: '52px', lg: '30px' },
              }}
            >
              {withSubmit ? (
                <Button
                  key="formSubmitButton"
                  color="info"
                  size="large"
                  sx={{
                    fontSize: '15px',
                    paddingY: { xs: '11px', sm: '19px' },
                  }}
                  variant="contained"
                  type="submit"
                >
                  {buttonText ||
                    t('welcomePage.loanApplicationPossible.liberis.checkYourData.buttonText')}
                </Button>
              ) : (
                <Button
                  key={`formButton-${liberisStage}`}
                  color="info"
                  size="large"
                  sx={{
                    fontSize: '15px',
                    paddingY: { xs: '11px', sm: '19px' },
                  }}
                  variant="contained"
                  type="button"
                  onClick={onButtonClickHandler}
                >
                  {buttonText ||
                    t('welcomePage.loanApplicationPossible.liberis.checkYourData.buttonText')}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
